/**
 * Game registry — pure metadata only.
 *
 * Deliberately free of React and `next/dynamic` so it can be imported by both
 * Server Components (e.g. to validate a `[slug]` route) and Client Components.
 * The actual lazy-loaded components live in `game-components.tsx`.
 */

export type Locale = 'en' | 'zh';

export interface LocalizedText {
  en: string;
  zh: string;
}

export interface GameMeta {
  /** URL segment under `/play/`. */
  slug: string;
  title: LocalizedText;
  description: LocalizedText;
  /** CSS background for the card cover (gradient or color). */
  accent: string;
  /** Emoji or short glyph shown on the card cover. */
  icon: string;
  tags: { en: string[]; zh: string[] };
}

export const GAMES: GameMeta[] = [
  {
    slug: 'stack-tower',
    title: { en: 'Stack Tower', zh: '堆叠高塔' },
    description: {
      en: 'A 3D precision game — time your drops to stack the tower as high as you can.',
      zh: '3D 精准堆叠小游戏——把握节奏放积木，看你能堆多高。',
    },
    accent: 'linear-gradient(135deg, #6a8dff 0%, #a86bff 100%)',
    icon: '🏗️',
    tags: { en: ['3D', 'Precision'], zh: ['3D', '精准'] },
  },
  {
    slug: '2048',
    title: { en: '2048', zh: '2048' },
    description: {
      en: 'Slide and merge numbered tiles. Combine them to reach 2048.',
      zh: '滑动并合并数字方块，挑战合成 2048！',
    },
    accent: 'linear-gradient(135deg, #f0b27a 0%, #e67e22 100%)',
    icon: '🔢',
    tags: { en: ['Puzzle', 'Casual'], zh: ['益智', '休闲'] },
  },
];

export const GAME_SLUGS: string[] = GAMES.map((g) => g.slug);

export function getGame(slug: string): GameMeta | undefined {
  return GAMES.find((g) => g.slug === slug);
}

export function pick<T>(value: LocalizedText, locale: Locale): string {
  return value[locale] ?? value.en;
}
